import {
  Box,
  Card,
  Stack,
  Typography,
  Divider,
  LinearProgress,
  Button,
  useTheme,
} from "@mui/material";
import {
  useAutomationIntegrationApiV1GetPlanningStepsQuery,
  useAutomationIntegrationApiV1GetPlanningProgressQuery,
  useAutomationIntegrationApiV1GetPlanGenerationQuery,
  useWebApiV1GetUserFeatureSwitchesQuery,
  PlanningStepSequence,
  PlanningProgress,
  useWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";
import { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { Timer } from "../components/PlanningAutomation/Timer";
import {
  ErrorBox,
  StatusPageDialog,
  WarningBox,
} from "../components/PlanningAutomation/StatusPageComponents";
import { Feature } from "@enums";
import {
  getElapsedTimeSubtext,
  getStatusColour,
  getAutomationStatus,
  getPlanProgressPercentage,
  AutomationStatus,
} from "@pages/OrderTracker/components/PlanningAutomation/utils";
import { Add } from "@mui/icons-material";
import CompletedPlans from "../components/PlanningAutomation/CompletedPlans";
import { useNavigate } from "react-router-dom";
import BottomActionBar from "@pages/Layout/BottomActionBar";

interface Props {
  orderId: number;
  disablePlanGeneration?: boolean;
}

const AutomatedPlanStatusPage = ({
  orderId,
  disablePlanGeneration = false,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { data: order } = useWebApiV1OrderQuery({ orderId });

  // fetch steps unless they've already been fetched
  const planningStepsRef = useRef<PlanningStepSequence | undefined>(undefined);

  const shouldPollSteps = !planningStepsRef.current?.steps.length;
  const { data: planningSteps } =
    useAutomationIntegrationApiV1GetPlanningStepsQuery(
      { orderId },
      // poll until steps are available
      { pollingInterval: shouldPollSteps ? 5000 : 0, skip: !shouldPollSteps },
    );

  useEffect(() => {
    planningStepsRef.current = planningSteps;
  }, [planningSteps]);

  const { data: completedPlans } =
    useAutomationIntegrationApiV1GetPlanGenerationQuery({
      orderId: orderId,
    });

  const { data: featureSwitchData } =
    useWebApiV1GetUserFeatureSwitchesQuery(undefined);

  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  // fetch progress until progress contains the generation result
  const planningProgressRef = useRef<PlanningProgress | undefined>(undefined);

  const shouldPollProgress =
    planningStepsRef.current?.steps.length &&
    !planningProgressRef.current?.result;

  const { data: planningProgressData } =
    useAutomationIntegrationApiV1GetPlanningProgressQuery(
      { orderId },
      {
        pollingInterval: shouldPollProgress ? 5000 : 0,
        // skip there are no steps or there is already a planning result
        skip: !shouldPollProgress,
      },
    );

  useEffect(() => {
    planningProgressRef.current = planningProgressData;
  }, [planningProgressData]);

  const [showStatusDialog, setShowStatusDialog] = useState(false);

  if (!order) {
    return <></>;
  }

  const planningProgress =
    planningProgressData || order.latestPlanGenerationAttempt;

  const startTime = dayjs(planningProgress?.planningStartedAt);

  const completedTime = dayjs(planningProgress?.result?.finishedAt);

  const automationStatus = planningProgress
    ? getAutomationStatus(planningProgress)
    : order?.latestPlanGenerationAttempt?.status;

  const elapsedTimeSubtext = getElapsedTimeSubtext(
    automationStatus as AutomationStatus,
  );

  const planProgressPercentage =
    planningSteps && planningProgress
      ? getPlanProgressPercentage(
          planningSteps.steps,
          planningProgress.currentStep,
        )
      : order?.latestPlanGenerationAttempt?.planProgressPercentage || 0;

  const planningAutomationCompleted =
    automationStatus === "success" || automationStatus === "error";

  const currentStepIndex =
    planningSteps?.steps.indexOf(planningProgress?.currentStep || "") ?? -1;

  return (
    <Stack
      sx={{
        gap: 4,
      }}
    >
      <Card>
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Stack
            sx={{
              padding: 2,
              width: 1,
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle1">
                Planning Automation
                {planningProgress?.templateName
                  ? `: ${planningProgress?.templateName}`
                  : ""}
              </Typography>
              <Typography variant="body2">Machine {order.machine}</Typography>
            </Stack>
            <Stack
              direction="row"
              sx={{
                gap: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {elapsedTimeSubtext}
              </Typography>
              {automationStatus !== AutomationStatus.NOT_STARTED && (
                <Timer startTime={startTime} completedTime={completedTime} />
              )}
            </Stack>
            <LinearProgress
              sx={{ marginBottom: 2, marginTop: 3 }}
              variant="determinate"
              value={planProgressPercentage}
              color={getStatusColour(automationStatus as AutomationStatus)}
            />
            {automationStatus !== AutomationStatus.NOT_STARTED && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {currentStepIndex + 1}/{planningSteps?.steps.length}{" "}
                {planningProgress?.currentStep}
              </Typography>
            )}
            {automationStatus === AutomationStatus.ERROR && (
              <ErrorBox
                error={planningProgress?.result?.error || ""}
                errorDate={planningProgress?.result?.finishedAt || ""}
                numberOfWarnings={planningProgress?.warnings?.length || 0}
                onClick={() => setShowStatusDialog(true)}
              />
            )}
            {automationStatus !== AutomationStatus.ERROR &&
            planningProgress?.warnings.length ? (
              <WarningBox
                onClick={() => setShowStatusDialog(true)}
                numberOfWarnings={planningProgress.warnings.length}
              />
            ) : null}
            <Divider sx={{ marginTop: 1, marginX: -2 }} />
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`/commandcenter/${order.id}`);
              }}
              disabled={!planningAutomationCompleted || disablePlanGeneration}
              sx={{ width: "max-content", paddingX: 2, marginTop: 2 }}
              startIcon={<Add />}
            >
              Generate another plan
            </Button>
          </Stack>
        </Box>
      </Card>

      {(planningAutomationCompleted || (completedPlans || []).length > 0) && (
        <>
          <Divider sx={{ width: 1 }} />
          <CompletedPlans plans={completedPlans || []} />
        </>
      )}

      <StatusPageDialog
        warnings={planningProgress?.warnings ?? []}
        finishedAt={planningProgress?.result?.finishedAt}
        open={showStatusDialog}
        onClose={() => setShowStatusDialog(false)}
      />
      <BottomActionBar>
        <Stack
          direction="row"
          sx={{
            width: 1,
            justifyContent: "flex-end",
            padding: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (showPlanReview) {
                navigate(`/commandcenter/${order.id}/plan-review`);
              } else {
                navigate(`/commandcenter/${order.id}/handover`);
              }
            }}
          >
            Next: {showPlanReview ? "Plan Review" : "Handover"}
          </Button>
        </Stack>
      </BottomActionBar>
    </Stack>
  );
};

export default AutomatedPlanStatusPage;
